/**
 * Frontend/Homepage — module constants
 *
 * Single home for the module's static values: UI labels, skeleton defaults,
 * cache tuning, carousel breakpoints, and placeholder counts. Components and
 * hooks import from here (or via the `lib` barrel for labels/defaults) instead
 * of hardcoding literals inline.
 */

import type { HomepageProductSlider, HomepagePromoBlock } from '../types'

// =============================================================================
// UI LABELS (Romanian) — centralized for consistency and easier i18n
// =============================================================================

export const LABELS = {
  // Price
  PRICE_SUFFIX: 'cu TVA',
  MSRP_PREFIX: 'PRP:',

  // Buttons
  ADD_TO_CART: 'Adauga in cos',
  VIEW_DETAILS: 'Vezi detalii',
  ADD_TO_WISHLIST: 'Adauga la favorite',
  REMOVE_FROM_WISHLIST: 'Sterge de la favorite',

  // Navigation
  NAV_PREV_SLIDE: 'Slide anterior',
  NAV_NEXT_SLIDE: 'Slide următor',

  // Error messages
  HOMEPAGE_LOADING_ERROR: 'Eroare la încărcarea datelor homepage.',
  HOMEPAGE_ERROR_FALLBACK: 'A apărut o eroare la încărcarea paginii.',

  // Category cards
  CATEGORY_SECTION_TITLE: 'Cumpără după categorii',
  BRAND_SECTION_TITLE: 'Cumpără după brand',

  // Section-level error messages
  BANNER_SLIDER_ERROR: 'Eroare la încărcarea bannerelor.',
  PRODUCT_SLIDER_ERROR: 'Eroare la încărcarea produselor.',
  PROMO_BLOCK_ERROR: 'Eroare la încărcarea blocului promoțional.',
  CATEGORY_CARDS_ERROR: 'Eroare la încărcarea categoriilor.',
  TRUST_BAR_ERROR: 'Eroare la afișarea barei de trust.',
  SEO_TEXT_ERROR: 'Eroare la afișarea conținutului editorial.',
  REVIEWS_CAROUSEL_ERROR: 'Eroare la afișarea recenziilor.',
  BRAND_CARDS_ERROR: 'Eroare la afișarea brandurilor.',

  // Reviews section labels
  REVIEWS_SECTION_TITLE: 'Ce spun clienții noștri',
  REVIEWS_AVERAGE_LABEL: 'Rating mediu:',
  REVIEWS_OUT_OF_5: '/ 5',
} as const

// =============================================================================
// SKELETON DEFAULTS — placeholder objects rendered by HomepageSections while
// the real config is loading
// =============================================================================

export const DEFAULT_PROMO_BLOCK: HomepagePromoBlock = {
  id: 0,
  title: '',
  image: null,
  link: null,
  description: null,
  sort_order: 0,
}

export const DEFAULT_PRODUCT_SLIDER: HomepageProductSlider = {
  id: 0,
  identifier: 'skeleton',
  title: '',
  source_type: 'category',
  source_value: null,
  products_count: 5,
  display_type: 'slider',
  sort_order: 0,
}

// =============================================================================
// CACHE DURATIONS — per-query overrides for slow-changing homepage data.
// The QueryClient singleton defaults to 5min stale / 10min gc
// (see modules/Infrastructure/lib/queryClient.ts); these bump it for data that
// changes rarely (config, reviews, category URLs).
// =============================================================================

/** staleTime for rarely-changing homepage data */
export const STATIC_DATA_STALE_TIME = 10 * 60 * 1000 // 10 minutes
/** gcTime for the same slow-changing data */
export const STATIC_DATA_GC_TIME = 30 * 60 * 1000 // 30 minutes

// =============================================================================
// CAROUSEL BREAKPOINTS — Swiper slidesPerView keyed by min viewport width (px)
// =============================================================================

/** Product slider: 2 / 3 / 5 across mobile / tablet / desktop */
export const PRODUCT_SLIDER_BREAKPOINTS: Record<number, { slidesPerView: number }> = {
  320: { slidesPerView: 2 },
  768: { slidesPerView: 3 },
  1280: { slidesPerView: 5 },
}

/** Reviews carousel: match compact product cards — 2 / 3 / 5 across mobile / tablet / desktop */
export const REVIEWS_CAROUSEL_BREAKPOINTS: Record<number, { slidesPerView: number }> = {
  320: { slidesPerView: 2 },
  768: { slidesPerView: 3 },
  1280: { slidesPerView: 5 },
}

// =============================================================================
// SKELETON COUNTS — placeholder item counts; each matches the widest
// grid/carousel row so the skeleton ≈ the loaded layout (less CLS)
// =============================================================================

export const SKELETON_COUNTS = {
  productSlider: 5,
  categoryCards: 5,
  brandCards: 5,
  reviewsCarousel: 5,
} as const
